<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="loanCreationSuccessCount.length || loanCreationFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="loanCreationSuccessCount.length"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ loanCreationSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="loanCreationFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="loanCreationFailedReport"
            :items="loanCreationFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="batchLoanRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onLoadXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>
        <b-col md="6">
          <b-row>
            <b-col md="5">
              <Button
                v-if="getUserPermission('BATCH_LOAN_UPLOAD') && parseExcelFile.length"
                class="mt-1"
                label="Next >"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate()"
              />
              <Button
                v-if="getUserPermission('BATCH_LOAN_UPLOAD') && !errorMessages.length"
                class="mt-1"
                :label="$t('Create Batch Loan')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate()"
              />
            </b-col>
            <b-col md="7">
              <Button
                class="ml-lg-2 mt-1"
                align="end"
                :label="$t('Download Batch Loan Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="batchLoanReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormFile, BTable, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import { toKobo } from '@/helpers/formatAmount'
import Button from '../../components/button/Button.vue'
import { batchLoanFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import removeSpecialCharExceptComma from '../../../helpers/removeSpecialCharExceptComma'
import { toYearWithDate } from '../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BTable,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      sortOrders: {},
      loanFormat: batchLoanFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true },
        { key: 'repaymentAccountNumber', sortable: true },
        { key: 'oldReferenceNumber', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'lendingModel', sortable: true },
        { key: 'economicSector', sortable: true },
        { key: 'guarantorID1', sortable: true },
        { key: 'guarantorID2', sortable: true },
        { key: 'penaltyGracePeriodInDays', sortable: true },
        { key: 'loanAmount' },
        { key: 'interestCommencementDate' },
        { key: 'hasCollateralPledge', sortable: true },
        { key: 'collateralType', sortable: true }, // This field is meant to be a drop down list.. Pls validate with backend
        { key: 'collateralValuation', sortable: true },
        { key: 'collateralDescription', sortable: true },
        { key: 'startDate', sortable: true },
        { key: 'principalRepaymentType', sortable: true },
        { key: 'principalFrequency', sortable: true },
        { key: 'principalToBeRepaidEvery', sortable: true },
        { key: 'numberOfPrincipalInstallment', sortable: true },
        { key: 'applicableInterestRate', sortable: true },
        { key: 'defaultingInterestRate', sortable: true },
        { key: 'interestRepaymentType', sortable: true },
        { key: 'interestFrequency', sortable: true },
        { key: 'interestToBeRepaidEvery', sortable: true },
        { key: 'numberOfInterestInstallment', sortable: true },
        { key: 'interestAccrualMode', sortable: true },
        { key: 'securityDeposit', sortable: true },
        { key: 'holdFundSecurityDeposit', sortable: true },
        { key: 'loanFeeCodeSeparatedByComma', sortable: true },
        { key: 'allowMoratorium', sortable: true },
        { key: 'moratoriumDuration', sortable: true },
        { key: 'moratoriumFrequency', sortable: true },
        { key: 'moratoriumEffect', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      errorMessage: '',
      loanCreationSuccessCount: [],
      loanCreationFailed: [],
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Old Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  compuuted: {
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchLoanAccountCreation: 'batch/batchLoanCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateBatchLoanCreation: 'batch/validateBatchLoanCreation',
    }),
    async handleCreate() {
      this.$refs.batchLoanRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchLoanCreationAction, { text: this.$t('Are you sure you want to Create Batch Loans?') })
        }
      })
    },
    async batchLoanCreationAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerID: item.customerID ? item.customerID.toString() : '',
          repaymentAccountNumber: item.repaymentAccountNumber ? item.repaymentAccountNumber.toString() : '',
          oldReferenceNumber: item.oldReferenceNumber ? item.oldReferenceNumber.toString() : '',
          productCode: item.productCode ? item.productCode.toString() : '',
          guarantorID1: item.guarantorID1 ? item.guarantorID1.toString() : '',
          guarantorID2: item.guarantorID2 ? item.guarantorID2.toString() : '',
          penaltyGracePeriodInDays: item.penaltyGracePeriodInDays ? Number(item.penaltyGracePeriodInDays) : 0,
          loanAmount: item.loanAmount ? toKobo(item.loanAmount) : 0,
          interestCommencementDate: item.interestCommencementDate ? toYearWithDate(item.interestCommencementDate) : null,
          hasCollateralPledge: item.hasCollateralPledge ? Boolean(item.hasCollateralPledge) : false,
          collateralValuation: item.collateralValuation ? toKobo(item.collateralValuation) : 0,
          startDate: item.startDate ? toYearWithDate(item.startDate) : null,
          collateralType: item.collateralType ? Number(item.collateralType) : 1,
          lendingModel: item.lendingModel ? Number(item.lendingModel) : 1,
          economicSector: item.economicSector ? Number(item.economicSector) : 41000,
          principalToBeRepaidEvery: item.principalToBeRepaidEvery ? Number(item.principalToBeRepaidEvery) : 0,
          principalFrequency: item.principalFrequency ? Number(item.principalFrequency) : 0,
          principalRepaymentType: item.principalRepaymentType ? Number(item.principalRepaymentType) : 0,
          numberOfPrincipalInstallment: item.numberOfPrincipalInstallment ? Number(item.numberOfPrincipalInstallment) : 0,
          applicableInterestRate: item.applicableInterestRate ? Number(item.applicableInterestRate) : 0,
          defaultingInterestRate: item.defaultingInterestRate ? Number(item.defaultingInterestRate) : 0,
          interestToBeRepaidEvery: item.interestToBeRepaidEvery ? Number(item.interestToBeRepaidEvery) : 0,
          interestFrequency: item.interestFrequency ? Number(item.interestFrequency) : 0,
          interestRepaymentType: item.interestRepaymentType ? Number(item.interestRepaymentType) : 0,
          interestAccrualMode: item.interestAccrualMode ? Number(item.interestAccrualMode) : 0,
          numberOfInterestInstallment: item.numberOfInterestInstallment ? Number(item.numberOfInterestInstallment) : 0,
          securityDeposit: item.securityDeposit ? toKobo(item.securityDeposit) : 0,
          holdFundSecurityDeposit: item.holdFundSecurityDeposit ? Boolean(item.holdFundSecurityDeposit) : false,
          loanFeeCodeSeparatedByComma: item.loanFeeCodeSeparatedByComma ? removeSpecialCharExceptComma(item.loanFeeCodeSeparatedByComma.toString()) : '',
          allowMoratorium: item.allowMoratorium ? Number(item.allowMoratorium) : null,
          moratoriumDuration: item.moratoriumDuration ? Number(item.moratoriumDuration) : 0,
          moratoriumFrequency: item.moratoriumFrequency ? item.moratoriumFrequency.toString() : null,
          moratoriumEffect: item.moratoriumEffect ? item.moratoriumEffect.toString() : null,
        }))
        const response = await this.batchLoanAccountCreation(formData)
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          this.loanCreationSuccessCount = data.success
          this.loanCreationFailed = data.failed
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errortext = this.$t('Your request to create Batch Loan failed. Reason')
          this.alertError({ text: `${errortext}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onLoadXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false }).map(row => Object.keys(row).reduce((obj, key) => {
            // eslint-disable-next-line no-param-reassign
            obj[key.trim()] = row[key]
            return obj
          }, {}))
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            customerID: items['Customer ID'],
            repaymentAccountNumber: items['Repayment Account Number'],
            oldReferenceNumber: items['Old Reference Number'],
            productCode: items['Product Code'],
            lendingModel: items['Lending Model'],
            economicSector: items['Economic Sector'],
            guarantorID1: items['Guarantor ID1'],
            guarantorID2: items['Guarantor ID2'],
            penaltyGracePeriodInDays: items['Penalty Grace Period In Days'],
            loanAmount: items['Loan Amount'],
            interestCommencementDate: items['Interest Commencement Date'],
            hasCollateralPledge: items['Has Collateral Pledge'],
            collateralType: items['Collateral Type'],
            collateralValuation: items['Collateral Valuation'],
            collateralDescription: items['Collateral Description'],
            startDate: items['Start Date'],
            principalRepaymentType: items['Principal Repayment Type'],
            principalFrequency: items['Principal Frequency'],
            principalToBeRepaidEvery: items['Principal To Be Repaid Every'],
            numberOfPrincipalInstallment: items['Number Of Principal Installment'],
            applicableInterestRate: items['Applicable Interest Rate'],
            defaultingInterestRate: items['Defaulting Interest Rate'],
            interestRepaymentType: items['Interest Repayment Type'],
            interestFrequency: items['Interest Frequency'],
            interestToBeRepaidEvery: items['Interest To Be Repaid Every'],
            numberOfInterestInstallment: items['Number Of Interest Installment'],
            interestAccrualMode: items['Interest Accrual Mode'],
            securityDeposit: items['Security Deposit'],
            holdFundSecurityDeposit: items['Hold Fund Security Deposit'],
            loanFeeCodeSeparatedByComma: items['Loan Fee Code Separated By Comma'],
            allowMoratorium: items['Allow Moratorium'],
            moratoriumDuration: items['Moratorium Duration'],
            moratoriumFrequency: items['Moratorium Frequency'],
            moratoriumEffect: items['Moratorium Effect'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerID: item.customerID,
          repaymentAccountNumber: item.repaymentAccountNumber,
          oldReferenceNumber: item.oldReferenceNumber,
          productCode: item.productCode,
          guarantorID1: item.guarantorID1,
          guarantorID2: item.guarantorID2,
          penaltyGracePeriodInDays: item.penaltyGracePeriodInDays ? Number(item.penaltyGracePeriodInDays) : 0,
          loanAmount: item.loanAmount ? toKobo(item.loanAmount) : 0,
          interestCommencementDate: item.interestCommencementDate ? toYearWithDate(item.interestCommencementDate) : null,
          hasCollateralPledge: item.hasCollateralPledge ? Boolean(item.hasCollateralPledge) : false,
          collateralValuation: item.collateralValuation ? toKobo(item.collateralValuation) : 0,
          startDate: item.startDate ? toYearWithDate(item.startDate) : null,
          collateralType: item.collateralType ? Number(item.collateralType) : 1,
          lendingModel: item.lendingModel ? Number(item.lendingModel) : 1,
          economicSector: item.economicSector ? Number(item.economicSector) : 41000,
          principalToBeRepaidEvery: item.principalToBeRepaidEvery ? Number(item.principalToBeRepaidEvery) : 0,
          numberOfPrincipalInstallment: item.numberOfPrincipalInstallment ? Number(item.numberOfPrincipalInstallment) : 0,
          principalFrequency: item.principalFrequency ? Number(item.principalFrequency) : 0,
          principalRepaymentType: item.principalRepaymentType ? Number(item.principalRepaymentType) : 0,
          applicableInterestRate: item.applicableInterestRate ? Number(item.applicableInterestRate) : 0,
          defaultingInterestRate: item.defaultingInterestRate ? Number(item.defaultingInterestRate) : 0,
          interestToBeRepaidEvery: item.interestToBeRepaidEvery ? Number(item.interestToBeRepaidEvery) : 0,
          numberOfInterestInstallment: item.numberOfInterestInstallment ? Number(item.numberOfInterestInstallment) : 0,
          interestRepaymentType: item.interestRepaymentType ? Number(item.interestRepaymentType) : 0,
          interestFrequency: item.interestFrequency ? Number(item.interestFrequency) : 0,
          interestAccrualMode: item.interestAccrualMode ? Number(item.interestAccrualMode) : 0,
          securityDeposit: item.securityDeposit ? toKobo(item.securityDeposit) : 0,
          holdFundSecurityDeposit: item.holdFundSecurityDeposit ? Boolean(item.holdFundSecurityDeposit) : false,
          loanFeeCodeSeparatedByComma: item.loanFeeCodeSeparatedByComma ? removeSpecialCharExceptComma(item.loanFeeCodeSeparatedByComma) : '',
          allowMoratorium: item.allowMoratorium ? Number(item.allowMoratorium) : null,
          moratoriumDuration: item.moratoriumDuration ? Number(item.moratoriumDuration) : 0,
          moratoriumFrequency: item.moratoriumFrequency ? Number(item.moratoriumFrequency) : null,
          moratoriumEffect: item.moratoriumEffect ? Number(item.moratoriumEffect) : null,
        }))
        const response = await this.validateBatchLoanCreation(formData)
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.alertSuccess({ text: this.$t('Everything seems good! Continue with batch loan creation') })
          }
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to create Batch Loan Account Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
        })
        this.isCreateBatchHidden = true
      } finally {
        this.isLoading = false
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Loan Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const BatchAccountFileTemplate = 2
        const response = await this.downloadAccountFormat(BatchAccountFileTemplate)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Loan-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Loan Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your request to download Batch Loan Format failed. Reason')
          this.alertError({ text: `${text}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>

<style scoped>
.successGB {
  color: rgb(86, 128, 25);
  font-size: 16px;
  }
.failedBG {
  color: #e23720;
  font-size: 16px;
}
</style>
